import { computed } from 'vue';
import { useI18n } from '@experience/i18n';
import { getAvailableFormat, getAvailableTourFormat } from '@/features/filter/format/lib/format';
import { ACCOMMODATION_VALUE, ACTIVITY_LEVEL_VALUE, COMFORT_VALUE, START_TIME_VALUE } from '@/shared/types/basic';
import formatRu from '@/features/filter/format/i18n/ru.json';
import durationIntervalRu from '@/features/filter/duration-interval/i18n/ru.json';
import startTimeRu from '@/entities/filter/start-time/i18n/ru.json';
import sortingRu from '@/features/filter/sorting/i18n/ru.json';
import comfortRu from '@/entities/filter/comfort/i18n/ru.json';
import activityLevelRu from '@/entities/filter/activity-level/i18n/ru.json';
import accommodationRu from '@/entities/filter/accommodation/i18n/ru.json';

const DURATION_SLUGS = ['upto_2_hours', 'from_2_to_4_hours', 'from_4_to_6_hours', 'from_6_hours'] as const;

const DURATION_I18N_KEYS = [
  'durationInterval.uptoTwoHours',
  'durationInterval.fromTwoToFourHours',
  'durationInterval.fromFourToSixHours',
  'durationInterval.fromSixHours',
] as const;

/**
 * Composable для построения карт лейблов для payload поиска Mindbox из i18n (единый источник истины).
 * Используется на страницах листинга экскурсий и туров при сборке Mindbox payload.
 */
export const useMindboxSearchLabelMaps = () => {
  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru: {
        ...formatRu,
        ...durationIntervalRu,
        ...startTimeRu,
        ...sortingRu,
        ...comfortRu,
        ...activityLevelRu,
        ...accommodationRu,
      },
    },
  });

  const formatLabels = computed<Record<string, string>>(() => {
    const exp = getAvailableFormat(t);
    const tour = getAvailableTourFormat(t);

    return {
      [exp.INDIVIDUAL.value]: exp.INDIVIDUAL.label,
      [exp.GROUP.value]: exp.GROUP.label,
      [exp.MINI_GROUP.value]: exp.MINI_GROUP.label,
      [tour.INDIVIDUAL.value]: tour.INDIVIDUAL.label,
      [tour.GROUP.value]: tour.GROUP.label,
      [tour.MINI_GROUP.value]: tour.MINI_GROUP.label,
    };
  });

  const durationLabels = computed<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    DURATION_SLUGS.forEach((slug, i) => {
      out[slug] = t(DURATION_I18N_KEYS[i]);
    });

    return out;
  });

  const startTimeLabels = computed<Record<string, string>>(() => ({
    [START_TIME_VALUE.MORNING]: t('morning'),
    [START_TIME_VALUE.DAY]: t('day'),
    [START_TIME_VALUE.EVENING]: t('evening'),
    [START_TIME_VALUE.NIGHT]: t('night'),
  }));

  const sortingLabels = computed<Record<string, string>>(() => ({
    rank: t('sorting.rank'),
    published_at: t('sorting.publishedAt'),
    price: t('sorting.price'),
    rating: t('sorting.rating'),
    discount: t('sorting.discount'),
  }));

  const comfortLabels = computed<Record<string, string>>(() => ({
    [COMFORT_VALUE.WILD]: t('wild'),
    [COMFORT_VALUE.SIMPLE]: t('simple'),
    [COMFORT_VALUE.STANDARD]: t('standard'),
    [COMFORT_VALUE.PREMIUM]: t('premium'),
  }));

  const activityLevelLabels = computed<Record<string, string>>(() => ({
    [ACTIVITY_LEVEL_VALUE.MINIMUM]: t('minimum'),
    [ACTIVITY_LEVEL_VALUE.EASY]: t('easy'),
    [ACTIVITY_LEVEL_VALUE.MEDIUM]: t('medium'),
    [ACTIVITY_LEVEL_VALUE.HARD]: t('hard'),
  }));

  const accommodationLabels = computed<Record<string, string>>(() => ({
    [ACCOMMODATION_VALUE.HOTEL_3]: t('hotel_3'),
    [ACCOMMODATION_VALUE.HOTEL_4_5]: t('hotel_4_5'),
    [ACCOMMODATION_VALUE.APARTMENTS]: t('apartments'),
    [ACCOMMODATION_VALUE.GUEST_HOUSE]: t('guest_house'),
    [ACCOMMODATION_VALUE.COTTAGE]: t('cottage_villa'),
    [ACCOMMODATION_VALUE.CAMPING]: t('camping'),
    [ACCOMMODATION_VALUE.GLAMPING]: t('glamping'),
    [ACCOMMODATION_VALUE.HOTEL_2]: t('hotel_2'),
    [ACCOMMODATION_VALUE.SPA_HOTEL]: t('spa_hotel'),
    [ACCOMMODATION_VALUE.UNIQUE]: t('unique'),
    [ACCOMMODATION_VALUE.YACHT_SHIP]: t('yacht_ship'),
    [ACCOMMODATION_VALUE.TRAIN]: t('train'),
    [ACCOMMODATION_VALUE.NOT_SET]: t('notSet'),
  }));

  return {
    formatLabels,
    durationLabels,
    startTimeLabels,
    sortingLabels,
    comfortLabels,
    accommodationLabels,
    activityLevelLabels,
  };
};
